import { useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { Deal, PipelineStage, Contact } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { formatCurrency } from '../../utils/currency'
import { gmailService } from '../../services/gmail'
import { STAGE_COMPOSE_SUBJECTS } from '../../types'
import { useAuthStore } from '../../stores/authStore'
import DealDetailPanel from './DealDetailPanel'

interface DealCardProps {
  deal: Deal
  stage: PipelineStage
}

const STAGE_BORDER_COLOUR: Record<PipelineStage, string> = {
  lead: 'border-l-stage-lead',
  conversation: 'border-l-stage-conversation',
  proposal: 'border-l-stage-proposal',
  negotiation: 'border-l-stage-negotiation',
  won: 'border-l-stage-won',
  lost: 'border-l-stage-lost',
  nurture: 'border-l-stage-nurture',
}

export default function DealCard({ deal, stage }: DealCardProps) {
  const { companies, contacts } = useDataStore()
  const { user } = useAuthStore()
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deal.id,
  })

  const company = companies.find((c) => c.id === deal.companyId)
  const companyContacts = contacts.filter((c) => c.companyId === deal.companyId && c.email)
  const primaryContact: Contact | undefined = companyContacts[0]

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
      }
    : undefined

  const handleQuickEmail = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!primaryContact?.email) return

    const firstName = primaryContact.name.split(' ')[0]
    const baseSubject = STAGE_COMPOSE_SUBJECTS[deal.stage]
    const subject = deal.stage === 'proposal' || deal.stage === 'negotiation'
      ? `${baseSubject} for ${deal.title}`
      : baseSubject

    const body = [
      `Hi ${firstName},`,
      '',
      getContextualOpening(deal.stage, deal.title),
      '',
      '',
      'Best regards,',
      user?.name || '',
    ].join('\n')

    const url = gmailService.getGmailComposeUrl({
      to: primaryContact.email,
      subject,
      body,
    })
    window.open(url, '_blank')
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        onClick={() => setIsDetailOpen(true)}
        onKeyDown={(e) => e.key === 'Enter' && setIsDetailOpen(true)}
        className={`
          deal-card ${STAGE_BORDER_COLOUR[stage]}
          ${isDragging ? 'deal-card--dragging' : ''}
          group
        `}
        role="button"
        tabIndex={0}
        aria-label={`Deal: ${deal.title}`}
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm text-text-primary mb-1 truncate flex-1">
            {deal.title}
          </h3>
          {primaryContact?.email && (
            <button
              onClick={handleQuickEmail}
              onPointerDown={(e) => e.stopPropagation()}
              className="opacity-0 group-hover:opacity-100 p-1 -m-1 text-text-secondary hover:text-action-primary transition-opacity duration-150"
              aria-label={`Quick email ${primaryContact.name}`}
              title={`Email ${primaryContact.name}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </button>
          )}
        </div>
        
        {company && (
          <p className="text-xs text-text-secondary mb-2 truncate">
            {company.name}
          </p>
        )}

        <div className="flex items-center justify-between">
          <span className="font-mono font-semibold text-sm text-text-primary">
            {formatCurrency(deal.value, deal.currency)}
          </span>
          
          {deal.status === 'stale' && (
            <span className="text-xs text-text-secondary bg-[#F5F5F3] px-1.5 py-0.5 rounded">
              Stale
            </span>
          )}
        </div>
      </div>

      <DealDetailPanel
        deal={deal}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </>
  )
}

function getContextualOpening(stage: PipelineStage, dealTitle: string): string {
  switch (stage) {
    case 'lead':
      return "I'd like to introduce myself and learn more about how we might work together."
    case 'conversation':
      return "Thank you for taking the time to speak with me. I wanted to follow up on our conversation."
    case 'proposal':
      return `I wanted to follow up on the proposal for ${dealTitle} and see if you have any questions.`
    case 'negotiation':
      return `I wanted to check in on ${dealTitle} and see if there's anything else you need from me.`
    case 'won':
      return "Thank you for choosing to work with me. I'm looking forward to getting started."
    case 'lost':
    case 'nurture':
      return "I hope you're doing well. I wanted to check in and see how things are going."
    default:
      return ''
  }
}
